import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "sonner";
import { GlobalConfetti } from "@/components/Confetti";
import NudgeAlarm from "@/components/NudgeAlarm";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfirmProvider } from "@/components/ConfirmProvider";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Taken from "@/pages/Taken";
import Eten from "@/pages/Eten";
import Groepen from "@/pages/Groepen";
import Meer from "@/pages/Meer";
import Tijdlijn from "@/pages/Tijdlijn";
import Tickets from "@/pages/Tickets";
import ContentModule from "@/pages/ContentModule";
import Settings from "@/pages/Settings";
import Kiosk from "@/pages/Kiosk";
import SharedShopping from "@/pages/SharedShopping";
import Join from "@/pages/Join";
import Ruilmarkt from "@/pages/Ruilmarkt";
import Rooster from "@/pages/Rooster";
import Klusjes from "@/pages/Klusjes";
import Aanmelden from "@/pages/Aanmelden";
import Goedkeuren from "@/pages/Goedkeuren";
import SetPassword from "@/pages/SetPassword";
import Abonnement from "@/pages/Abonnement";
import Hoppa from "@/pages/Hoppa";
import Nieuws from "@/pages/Nieuws";
import Punten from "@/pages/Punten";
import KlussenPool from "@/pages/KlussenPool";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import Landing from "@/pages/Landing";
import SuperAdminLogin from "@/pages/superadmin/SuperAdminLogin";
import SuperAdminPortal from "@/pages/superadmin/SuperAdminPortal";
import DeepLink from "@/pages/DeepLink";

function AuthCallback() {
  const navigate = useNavigate();
  const { googleSession } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const sessionId = hash.get("session_id");
    (async () => {
      try {
        await googleSession(sessionId);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/vandaag");
      } catch {
        navigate("/login");
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center">
      <p className="font-head font-black text-2xl animate-pulse">Inloggen…</p>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading, orgs, orgsLoaded } = useAuth();
  if (loading || user === null) return <div className="min-h-screen bg-brand-cream flex items-center justify-center"><p className="font-head font-black text-2xl animate-pulse">Laden…</p></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!orgsLoaded) return <div className="min-h-screen bg-brand-cream flex items-center justify-center"><p className="font-head font-black text-2xl animate-pulse">Laden…</p></div>;
  if (orgs.length === 0) return <Navigate to="/aanmelden" replace />;
  return children;
}

function RootGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen bg-brand-cream flex items-center justify-center"><p className="font-head font-black text-2xl animate-pulse">Laden…</p></div>;
  if (user) return <Navigate to="/vandaag" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const onPaywall = () => navigate("/abonnement");
    window.addEventListener("doenies-paywall", onPaywall);
    return () => window.removeEventListener("doenies-paywall", onPaywall);
  }, [navigate]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/kiosk" element={<Kiosk />} />
      <Route path="/lijst/:token" element={<SharedShopping />} />
      <Route path="/join/:code" element={<Join />} />
      <Route path="/t/:token" element={<DeepLink />} />
      <Route path="/aanmelden" element={<Aanmelden />} />
      <Route path="/goedkeuren/:reqId" element={<Goedkeuren />} />
      <Route path="/set-password/:token" element={<SetPassword />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/superadmin/login" element={<SuperAdminLogin />} />
      <Route path="/superadmin" element={<SuperAdminPortal />} />
      <Route path="/" element={<RootGate />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/vandaag" element={<Dashboard />} />
        <Route path="/taken" element={<Taken />} />
        <Route path="/eten" element={<Eten />} />
        <Route path="/groepen" element={<Groepen />} />
        <Route path="/meer" element={<Meer />} />
        <Route path="/tickets" element={<Tickets />} />
        <Route path="/tijdlijn" element={<Tijdlijn />} />
        <Route path="/ruilmarkt" element={<Ruilmarkt />} />
        <Route path="/rooster" element={<Rooster />} />
        <Route path="/klusjes" element={<Klusjes />} />
        <Route path="/hoppa" element={<Hoppa />} />
        <Route path="/nieuws" element={<Nieuws />} />
        <Route path="/punten" element={<Punten />} />
        <Route path="/klussen-pool" element={<KlussenPool />} />
        <Route path="/abonnement" element={<Abonnement />} />
        <Route path="/meer/:module" element={<ContentModule />} />
        <Route path="/instellingen" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
          <Toaster position="top-center" toastOptions={{ style: { border: "1.5px solid rgba(27,20,64,0.08)", borderRadius: "16px", fontFamily: "Outfit", fontWeight: 700, boxShadow: "0 18px 40px -18px rgba(124,92,255,0.5)" } }} />
          <GlobalConfetti />
          <NudgeAlarm />
          <AppRouter />
        </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}
